import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Dna,
  LineChart,
  Link2,
  Loader2,
  RefreshCw,
  Settings as SettingsIcon,
  Shield,
} from 'lucide-react';
import { isTrendVizUiEnabled } from './trend-viz/flag';
import { AccountMenu } from './AccountMenu';
import { showAdminSwitch, subscribeIamAccess } from '../auth/iamAccess';
import { usePatientPipelineStatus } from './patient/usePatientPipelineStatus';
import {
  getStoredSmartHistoryWindow,
  SMART_HISTORY_WINDOW_CHANGED_EVENT,
  SMART_HISTORY_WINDOW_LABELS,
} from '../api/smartApi';
import { cn } from './ui/utils';

type CommandApiStatus = 'idle' | 'checking' | 'connected' | 'disconnected';

/**
 * #107 (2026-08-01, USER ORDER): the appbar had grown into a flat row of
 * eleven unrelated items in no particular order, wearing three different
 * shapes assigned by nothing. It is now four zones, left to right, and a
 * SHAPE PER FUNCTION so an item's job is readable at a glance:
 *
 *   1. Identity   — who this chart belongs to, and the history window in force.
 *   2. Status     — read-only readouts. Pill, muted, never clickable.
 *   3. Navigation — links to another workspace. Pill, bordered, clickable.
 *   4. Controls   — act on this workspace. Square icon button.
 *
 * Two items were removed rather than moved, both pure duplicates:
 *   - `DevAuthStatus` (name + roles + Sign out) — `AccountMenu`, already in
 *     this header, renders exactly the same three things. Its role list was
 *     widened to match what the inline chip used to show, so nothing is lost.
 *   - the theme dropdown — theme already lives in workspace Settings
 *     (`SettingsModal.tsx`), reachable from the gear one place to its left.
 */

/** Zone 3. Navigation to another workspace: bordered pill, icon + label. */
const NAV_PILL_CLASS =
  'inline-flex h-8 items-center gap-1.5 rounded-full border border-border bg-background px-2.5 ' +
  'text-[11px] font-medium text-text-secondary transition-colors hover:bg-surface-hover ' +
  'hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring';

/**
 * Zone 4. Acts on this workspace: square icon button, no label.
 *
 * `AccountMenu` wears this too, via its `className` extension point. Its own
 * trigger is styled for the sidebar rail (h-9 w-9, rounded-xl, sidebar
 * palette), so every one of those utilities needs a counterpart here for
 * tailwind-merge to override it — `bg-transparent` included, or the sidebar's
 * `bg-sidebar-accent/60` survives and the avatar reads as a filled button
 * beside a flat gear. Verified against tailwind-merge directly, not assumed.
 */
const ICON_BUTTON_CLASS =
  'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-transparent ' +
  'bg-transparent text-text-tertiary transition-colors hover:border-border hover:bg-surface-raised ' +
  'hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring';

/** Hairline between zones, so the grouping is visible and not just spacing. */
function ZoneDivider() {
  return <span aria-hidden className="h-5 w-px shrink-0 bg-border" />;
}

interface HeaderProps {
  /** Opens the patient sheet (record + digital twin) from the patient chip. */
  onOpenPatientSheet?: () => void;
  /** Opens the notifications panel from the bell. */
  /** Opens workspace settings from the gear. */
  onOpenSettings?: () => void;
  commandApiEnabled?: boolean;
  commandApiStatus?: CommandApiStatus;
  patientName?: string;
  patientMrn?: string;
  /** Backend patient id for the pipeline "running" pill (SMART-connected only). */
  patientId?: string;
  hideAdminLink?: boolean;
}

/**
 * Background-pipeline visibility: the twin build keeps running
 * server-side when the clinician navigates away; this pill keeps that state
 * visible from the appbar. Quiet when ready; spinner while preparing; amber
 * with a retry affordance on failure (never color-only).
 */
function PipelinePill({ patientId }: { patientId: string }) {
  const { status, error, reload } = usePatientPipelineStatus(patientId);

  if (error) {
    return (
      <button
        type="button"
        onClick={() => void reload()}
        className="flex h-8 cursor-pointer items-center gap-1.5 rounded-full border border-(--status-attention)/40 bg-status-attention-bg px-2.5 text-[11px] font-medium text-status-attention-text transition-colors hover:bg-status-attention-bg/70"
        title={`Patient data sync issue: ${error}. Click to re-check.`}
      >
        <AlertTriangle className="size-3.5" aria-hidden />
        <span className="hidden sm:inline">Sync issue — retry</span>
        <RefreshCw className="size-3 sm:hidden" aria-hidden />
      </button>
    );
  }
  if (!status || status.ready) return null;
  const label = status.stage === 'scoring' ? 'Scoring organs…' : 'Building digital twin…';
  return (
    <span
      className="flex h-8 items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 text-[11px] font-medium text-primary"
      title={`${label} This continues in the background even if you leave the page.`}
      role="status"
    >
      <Loader2 className="size-3.5 animate-spin" aria-hidden />
      <span className="hidden sm:inline">{label}</span>
    </span>
  );
}

/**
 * The chart-history window in force for this session, always visible at the
 * top of the workspace. USER REQUEST (2026-07-31): once in the chat, nothing
 * told the clinician which window they had picked -- add a stylish,
 * high-contrast badge so it is never a mystery mid-conversation.
 *
 * Reads `sessionStorage` via `getStoredSmartHistoryWindow` and re-reads on
 * `SMART_HISTORY_WINDOW_CHANGED_EVENT` -- storage writes are silent to the
 * writing tab, so the entry gate's own `storeSmartHistoryWindow` dispatches
 * that event precisely so this badge can react without polling.
 */
function HistoryWindowBadge() {
  const [historyWindow, setHistoryWindow] = useState(() => getStoredSmartHistoryWindow());

  useEffect(() => {
    const onChange = () => setHistoryWindow(getStoredSmartHistoryWindow());
    window.addEventListener(SMART_HISTORY_WINDOW_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(SMART_HISTORY_WINDOW_CHANGED_EVENT, onChange);
  }, []);

  if (!historyWindow) return null;
  const label = SMART_HISTORY_WINDOW_LABELS[historyWindow];

  return (
    <span
      className="inline-flex h-8 items-center gap-1.5 rounded-full border border-brand-border bg-brand-subtle px-3 text-[11px] font-bold uppercase tracking-wide text-brand-text"
      title={`This session is scoped to ${label} of chart history. Close and reopen the app to change it.`}
      role="status"
    >
      {label}
    </span>
  );
}

export function Header({
  commandApiEnabled = false,
  commandApiStatus = 'idle',
  patientName = '',
  patientMrn = '',
  patientId = '',
  hideAdminLink = false,
  onOpenPatientSheet,
  onOpenSettings,
}: HeaderProps) {
  const [, setIamAccessRevision] = useState(0);
  const hideAdmin = hideAdminLink || !showAdminSwitch();

  useEffect(() => {
    return subscribeIamAccess(() => {
      setIamAccessRevision((revision) => revision + 1);
    });
  }, []);

  const initials = patientName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

  const showTrends = Boolean(patientMrn?.trim()) && isTrendVizUiEnabled();
  const showTwin = Boolean(patientMrn?.trim());
  const hasNav = showTwin || showTrends || !hideAdmin;
  const hasStatus = Boolean(patientId) || commandApiEnabled;

  return (
    <header className="z-10 flex h-[52px] shrink-0 items-center justify-between gap-3 border-b border-border bg-background/95 px-3 backdrop-blur-sm sm:px-4">
      {/* Patient identity — the always-visible clinical safety banner. */}
      {patientName ? (
        <button
          type="button"
          onClick={onOpenPatientSheet}
          className="group flex min-w-0 max-w-[46%] cursor-pointer items-center gap-2.5 rounded-lg border border-transparent px-1.5 py-1 text-left transition-colors hover:border-border hover:bg-surface-hover/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:max-w-sm"
          aria-label={`Current patient ${patientName}, MRN ${patientMrn}. Open patient overview`}
        >
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-[11px] font-medium tracking-tight text-primary"
            aria-hidden
          >
            {initials}
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[13px] font-medium leading-tight text-foreground">{patientName}</span>
            <span className="flex items-center gap-1.5 text-[10px] leading-tight text-text-tertiary">
              <span className="truncate font-mono tabular-nums" title={patientMrn}>MRN {patientMrn}</span>
              <span className="hidden items-center gap-1 text-status-positive-text sm:inline-flex">
                <Link2 className="size-3" aria-hidden />
                SMART
              </span>
            </span>
          </span>
        </button>
      ) : (
        <div className="min-w-0 flex-1" />
      )}

      {/* Zone 1 (cont.) — the history window in force, beside the name it scopes. */}
      {patientName ? <HistoryWindowBadge /> : null}

      <div className="flex shrink-0 items-center gap-2">
        {/* ── Zone 2: STATUS ──────────────────────────────────────────────
            Read-only readouts of what the system is doing. Muted pills, no
            hover, never clickable — so nothing here invites a click that
            does nothing. */}
        {hasStatus ? (
          <div className="flex items-center gap-1.5" role="group" aria-label="System status">
            {patientId ? <PipelinePill patientId={patientId} /> : null}
            {commandApiEnabled && (
              <div
                className={cn(
                  'flex h-8 items-center gap-1.5 rounded-full border px-2.5 text-[10px] font-medium sm:text-[11px]',
                  commandApiStatus === 'connected'
                    ? 'border-(--status-positive)/25 bg-status-positive-bg text-status-positive-text'
                    : commandApiStatus === 'checking'
                      ? 'border-(--status-attention)/25 bg-status-attention-bg text-status-attention-text'
                      : 'border-(--status-negative)/25 bg-status-negative-bg text-status-negative-text',
                )}
                role="status"
                title={
                  commandApiStatus === 'connected'
                    ? 'Command API connected and healthy'
                    : commandApiStatus === 'checking'
                      ? 'Verifying Command API connection…'
                      : 'Command API unavailable'
                }
              >
                <span
                  className={cn(
                    'inline-block size-2 shrink-0 rounded-full',
                    commandApiStatus === 'connected'
                      ? 'bg-emerald-500'
                      : commandApiStatus === 'checking'
                        ? 'animate-pulse bg-amber-500'
                        : 'bg-red-500',
                  )}
                  aria-hidden
                />
                <span className="hidden font-medium sm:inline">
                  {commandApiStatus === 'connected' ? 'API Ready' : commandApiStatus === 'checking' ? 'Checking…' : 'API Offline'}
                </span>
              </div>
            )}
          </div>
        ) : null}

        {hasStatus && hasNav ? <ZoneDivider /> : null}

        {/* ── Zone 3: NAVIGATION ──────────────────────────────────────────
            Every link out of this workspace, one shape, side by side.
            The twin and trends links are the ONE-click route to the
            patient's clinical picture; reaching the twin used to take three
            steps (open the drawer, find an 11px text link under the summary,
            follow it), which the console reported as "too many steps to reach
            the information you need". The drawer link stays; this is the
            direct route, beside the name it belongs to. */}
        {hasNav ? (
          <nav className="flex items-center gap-1.5" aria-label="Workspaces">
            {showTwin ? (
              <Link
                to={`/patient/${encodeURIComponent(patientMrn)}/digital-twin-v2`}
                className={NAV_PILL_CLASS}
                title="Organ health, social context, and trajectory narratives for this patient"
              >
                <Dna className="size-3.5" aria-hidden />
                <span className="hidden sm:inline">Digital twin</span>
              </Link>
            ) : null}
            {/* Trend/Record Visualization (register §10.2) — labs, vitals,
                conditions, medications and procedures on one timeline. */}
            {showTrends ? (
              <Link
                to={`/patient/${encodeURIComponent(patientMrn)}/trend-viz`}
                className={NAV_PILL_CLASS}
                title="Plot labs, vitals, conditions, medications and procedures on one timeline"
              >
                <LineChart className="size-3.5" aria-hidden />
                <span className="hidden sm:inline">Trends</span>
              </Link>
            ) : null}
            {!hideAdmin && (
              <Link
                to="/admin"
                aria-label="Admin workspace"
                className={NAV_PILL_CLASS}
                title="Admin console (separate workspace)"
              >
                <Shield className="size-3.5" aria-hidden />
                <span className="hidden lg:inline">Admin</span>
              </Link>
            )}
          </nav>
        ) : null}

        {hasNav ? <ZoneDivider /> : null}

        {/* ── Zone 4: CONTROLS ────────────────────────────────────────────
            Acts on this workspace. Square icon buttons, identical shape.
            The theme switcher used to sit here as a third button; it lives
            in workspace Settings (behind the gear), so it was a second door
            to one room and is gone. */}
        <div className="flex items-center gap-1">
          {onOpenSettings ? (
            <button
              type="button"
              onClick={onOpenSettings}
              className={ICON_BUTTON_CLASS}
              aria-label="Workspace settings"
              title="Workspace settings, including light/dark theme"
            >
              <SettingsIcon className="size-4" aria-hidden />
            </button>
          ) : null}
          {/* Identity lives here and only here: name, roles, and Sign out.
              The old inline DevAuthStatus chip duplicated all three. */}
          <AccountMenu variant="icon" side="bottom" align="end" className={ICON_BUTTON_CLASS} />
        </div>
      </div>
    </header>
  );
}
